import React, { useMemo, useState, useRef } from 'react';
import Box from '@material-ui/core/Box';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import Popover from '@material-ui/core/Popover';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faPlusSquare,
  faCheckCircle,
} from '@fortawesome/free-regular-svg-icons';

import { theme, colors } from '@picardy/picardy-design-system';

import type { ArrayElement } from '../../../util/types';
import SectionHeaderBar from '../../../common/components/SectionHeaderBar';
import { CourseGraphQL } from '../../../api/content';
import { useUser } from '../User';
import MiniCurriculumTopicEditor from './MiniCurriculumTopicEditor';

export interface Props {
  topic: ArrayElement<CourseGraphQL['topicsEntities']>;
  hideEducatorFeatures?: boolean;
}

export default function TopicDetail({
  topic,
  hideEducatorFeatures = false,
}: Props) {
  const user = useUser();
  const [addToCurriculumOpen, setAddToCurriculumOpen] = useState(false);
  const topicHeaderRef = useRef<HTMLDivElement>();
  const addToCurriculumPopoverId = `add-to-curriculum-${topic._id}`;

  const isAdmin = useMemo(() => user?.roles?.superuser, [user]);
  const isEducator = useMemo(() => user?.roles?.teacher, [user]);
  const showEducatorFeatures = isEducator && !hideEducatorFeatures;

  return (
    <Box marginBottom={4}>
      <SectionHeaderBar title={topic.title} ref={topicHeaderRef}>
        {showEducatorFeatures && (
          <Link
            aria-label="Add topic to a curriculum"
            variant="body1"
            style={{ color: theme.palette.grey['100'] }}
            component="button"
            onClick={() => setAddToCurriculumOpen(true)}
          >
            <FontAwesomeIcon icon={faPlusSquare} />
          </Link>
        )}
      </SectionHeaderBar>

      <Popover
        id={addToCurriculumPopoverId}
        open={addToCurriculumOpen}
        anchorEl={() => topicHeaderRef.current!}
        onClose={() => setAddToCurriculumOpen(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MiniCurriculumTopicEditor
          topic={topic}
          onError={() => setAddToCurriculumOpen(false)}
        />
      </Popover>

      {topic.lessonsEntities.map(lesson => (
        <Box
          key={lesson._id}
          display="flex"
          flexDirection="column"
          marginBottom={1}
          paddingLeft={2}
          paddingRight={2}
        >
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Link variant="h5" href={lesson.url}>
              {lesson.title}
              {lesson.completed && (
                <>
                  &nbsp;
                  <FontAwesomeIcon
                    color={colors.picardySuccessGreen}
                    icon={faCheckCircle}
                    aria-label="Completed lesson"
                  />
                </>
              )}
            </Link>

            {isAdmin && (
              <Link
                href={lesson.adminUrl}
                aria-label="Edit lesson in Picardy Admin"
              >
                <FontAwesomeIcon icon={faEdit} />
              </Link>
            )}
          </Box>

          <Typography
            variant="body1"
            component="div"
            dangerouslySetInnerHTML={{ __html: lesson.html }}
          />
        </Box>
      ))}
    </Box>
  );
}
